import React from 'react';
import { RouteObject } from 'react-router-dom';
import { PageType } from '@redux/analytics/analyticsTypes';
import ChangePassword from 'containers/Account/containers/ChangePassword/ChangePassword';
import CreatePassword from 'containers/Account/containers/CreatePassword/CreatePassword';
import ForgotPassword from 'containers/Account/containers/ForgotPassword/ForgotPassword';
import ForgotPasswordEmail from 'containers/Account/containers/ForgotPasswordEmail/ForgotPasswordEmail';
import InviteAccount from 'containers/Account/containers/InviteAccount/InviteAccount';
import PhoneVerification from 'containers/Account/containers/PhoneVerification/PhoneVerification';
import SuccessAuth from 'containers/Account/containers/SuccessAuth/SuccessAuth';
import AdminOrganizations from 'containers/AdminOrganizations/AdminOrganizations';
import AdminPermissionRoles from 'containers/AdminPermissionRoles/AdminPermissionRoles';
import AdminUserPermissions from 'containers/AdminUserPermissions/AdminUserPermissions';
import AllContent from 'containers/AllContent/AllContent';
import AllFlags from 'containers/AllFlags/AllFlags';
import AllPatients from 'containers/AllPatients/AllPatients';
import AllTracks from 'containers/AllTracks/AllTracks';
import AssignCarePlan from 'containers/AssignCarePlan/AssignCarePlan';
import CareGroup from 'containers/CareGroup/CareGroup';
import CareTeam from 'containers/CareTeam/CareTeam';
import ConfirmationPage from 'containers/ConfirmationPage/ConfirmationPage';
import ContentManager from 'containers/ContentManager/ContentManager';
import DeleteAccount from 'containers/DeleteAccount/DeleteAccount';
import DeleteAccountConfirm from 'containers/DeleteAccountConfirm/DeleteAccountConfirm';
import DemoAccessCodes from 'containers/DemoAccessCodes/DemoAccessCodes';
import DeviceContentDetails from 'containers/DeviceContentDetails/DeviceContentDetails';
import DeviceFactoryResetHistory from 'containers/DeviceFactoryResetHistory/DeviceFactoryResetHistory';
import DeviceUnlinkHistory from 'containers/DeviceUnlinkHistory/DeviceUnlinkHistory';
import DeviceOverview from 'containers/DeviceOverview/DeviceOverview';
import DeviceProvisioningOverview from 'containers/DeviceProvisioningOverview/DeviceProvisioningOverview';
import FirmwareVersionOverview from 'containers/FirmwareVersionOverview/FirmwareVersionOverview';
import FlagDetails from 'containers/FlagDetails/FlagDetails';
import Home from 'containers/Home/Home';
import Inbox from 'containers/Inbox/Inbox';
import KokoMap from 'containers/KokoMap/KokoMap';
import LLMPromptsManager from 'containers/LLMPromptsManager/LLMPromptsManager';
import LoginWrapper from 'containers/Login/LoginWrapper';
import ManageGroupPlans from 'containers/ManageGroupPlans/ManageGroupPlans';
import ManageOrganizationGeneral from 'containers/ManageOrganization/General/General';
import ManageOrganizationLayout from 'containers/ManageOrganization/ManageOrganizationLayout/ManageOrganizationLayout';
import ManageOrganizationMembers from 'containers/ManageOrganization/Members/Members';
import NewActivity from 'containers/NewActivity/NewActivity';
import NewDTCPatient from 'containers/NewDTCPatient/NewDTCPatient';
import NewEditPatient from 'containers/NewEditPatient/NewEditPatient';
import NewTeamMember from 'containers/NewTeamMember/NewTeamMember';
import PageDetails from 'containers/PageDetails/PageDetails';
import PatientDashboard from 'containers/PatientDashboard/PatientDashboard';
// import PersonPermissions from 'containers/PersonPermissions/PersonPermissions';
import PersonProgram from 'containers/PersonProgram/PersonProgram';
import SleepScheduleReview from 'containers/SleepScheduleReview/SleepScheduleReview';
import Tasks from 'containers/Tasks/Tasks';
import Track from 'containers/Track/Track';
import UnreadSleepCoachMessages from 'containers/UnreadSleepCoachMessages/UnreadSleepCoachMessages';
import UserDetails from 'containers/UserDetails/UserDetails';
import UserOverview from 'containers/UserOverview/UserOverview';
import Users from 'containers/Users/Users';
import withAnalyticsPageEvents from 'containers/withAnalyticsPageEvents/withAnalyticsPageEvents';
import { isProductionEnvironment } from 'utils/environment';
import { RouteNames } from 'utils/navigationUtils';
import AdminSideBar from './AdminSideBar/AdminSideBar';
import ProtectedRoute from './ProtectedRoute';
import RedirectIfLoggedInRoute from './RedirectIfLoggedInRoute';
import RouteNotFound from './RouteNotFound';

const LoginPage = withAnalyticsPageEvents(PageType.LOGIN)(LoginWrapper);
const ForgotPasswordPage = withAnalyticsPageEvents(PageType.FORGOT_PASSWORD)(ForgotPassword);
const ForgotPasswordEmailPage = withAnalyticsPageEvents(PageType.FORGOT_PASSWORD_EMAIL)(ForgotPasswordEmail);
const ResetPasswordPage = withAnalyticsPageEvents(PageType.RESET_PASSWORD_BY_TOKEN)(ChangePassword);
const CreatePasswordPage = withAnalyticsPageEvents(PageType.CREATE_PASSWORD)(CreatePassword);
const InviteAccountPage = withAnalyticsPageEvents(PageType.RESET_PASSWORD_BY_TOKEN)(InviteAccount);
const PhoneVerificationPage = withAnalyticsPageEvents(PageType.INVITE_PHONE_VERIFICATION)(PhoneVerification);
const SuccessAuthPage = withAnalyticsPageEvents(PageType.AUTH_SUCCESS)(SuccessAuth);
const DeleteAccountPage = withAnalyticsPageEvents(PageType.DELETE_ACCOUNT)(DeleteAccount);
const DeleteAccountConfirmPage = withAnalyticsPageEvents(PageType.DELETE_ACCOUNT_CONFIRM)(DeleteAccountConfirm);

const MainMenuPage = withAnalyticsPageEvents(PageType.MAIN_MENU)(Home);
const KokoMapPage = withAnalyticsPageEvents(PageType.KOKO_MAP)(KokoMap);
const UsersPage = withAnalyticsPageEvents(PageType.USERS)(Users);
const AllUsersPage = withAnalyticsPageEvents(PageType.ALL_USERS)(UserOverview);
const FlaggedUsersPage = withAnalyticsPageEvents(PageType.FLAGGED_USERS)(UserOverview);
const UserDetailsPage = withAnalyticsPageEvents(PageType.USER_DETAILS)(UserDetails);
const UserDetailsSleepCoachChatPage = withAnalyticsPageEvents(PageType.USER_DETAILS_SLEEP_COACH_CHAT)(UserDetails);
const DemoAccessCodesPage = withAnalyticsPageEvents(PageType.DEMO_ACCESS_CODES)(DemoAccessCodes);
const DeviceProvisioningPage = withAnalyticsPageEvents(PageType.DEVICE_PROVISIONING)(DeviceProvisioningOverview);
const FirmwareVersionsPage = withAnalyticsPageEvents(PageType.FIRMWARE_VERSIONS)(FirmwareVersionOverview);
const DeviceOverviewPage = withAnalyticsPageEvents(PageType.DEVICE_OVERVIEW)(DeviceOverview);
const DeviceFactoryResetHistoryPage = withAnalyticsPageEvents(PageType.DEVICE_FACTORY_RESET_HISTORY)(DeviceFactoryResetHistory);
const DeviceUnlinkHistoryPage = withAnalyticsPageEvents(PageType.DEVICE_UNLINK_HISTORY)(DeviceUnlinkHistory);

const EmailConfirmationTokenPage = withAnalyticsPageEvents(PageType.EMAIL_CONFIRMATION_TOKEN)(ConfirmationPage);
const ContentManagerPage = withAnalyticsPageEvents(PageType.CONTENT_MANAGER)(ContentManager);
const ContentAllTracksPage = withAnalyticsPageEvents(PageType.CONTENT_ALL_TRACKS)(AllTracks);
const ContentAllContentPage = withAnalyticsPageEvents(PageType.CONTENT_ALL_CONTENT)(AllContent);
const ContentTrackPage = withAnalyticsPageEvents(PageType.CONTENT_TRACK)(Track);
const ContentDeviceContentDetailsPage = withAnalyticsPageEvents(PageType.CONTENT_DEVICE_CONTENT_DETAILS)(DeviceContentDetails);
const ContentPageDetailsUpdatePage = withAnalyticsPageEvents(PageType.CONTENT_PAGE_DETAILS_UPDATE)(PageDetails);
const ContentPageDetailsAddPage = withAnalyticsPageEvents(PageType.CONTENT_PAGE_DETAILS_ADD)(PageDetails);
const FlagDetailsPage = withAnalyticsPageEvents(PageType.FLAG_DETAILS)(FlagDetails);
const AllFlagsPage = withAnalyticsPageEvents(PageType.ALL_FLAGS)(AllFlags);
const NewSleepCoachChatsPage = withAnalyticsPageEvents(PageType.NEW_SLEEP_COACH_CHATS)(UnreadSleepCoachMessages);
const SleepSchedulesReviewPage = withAnalyticsPageEvents(PageType.SLEEP_SCHEDULE_REVIEW)(SleepScheduleReview);

const AllPatientsPage = withAnalyticsPageEvents(PageType.ALL_PATIENTS)(AllPatients);
const TasksPage = withAnalyticsPageEvents(PageType.TASKS)(Tasks);
const InboxPage = withAnalyticsPageEvents(PageType.INBOX)(Inbox);
const PatientDashboardPage = withAnalyticsPageEvents(PageType.PATIENT_DASHBOARD)(PatientDashboard);
const EditPatientPage = withAnalyticsPageEvents(PageType.EDIT_PATIENT)(NewEditPatient);
const NewPatientPage = withAnalyticsPageEvents(PageType.NEW_PATIENT)(NewEditPatient);
const NewDTCPatientPage = withAnalyticsPageEvents(PageType.NEW_DTC_PATIENT)(NewDTCPatient);
const NewTeamMemberPage = withAnalyticsPageEvents(PageType.NEW_TEAM_MEMBER)(NewTeamMember);
const AssignCarePlanPage = withAnalyticsPageEvents(PageType.ASSIGN_CARE_PLAN)(AssignCarePlan);
const NewActivityPage = withAnalyticsPageEvents(PageType.NEW_ACTIVITY)(NewActivity);
const CareTeamPage = withAnalyticsPageEvents(PageType.CARE_TEAM)(CareTeam);
const CareGroupPage = withAnalyticsPageEvents(PageType.CARE_GROUP)(CareGroup);
const ManageGroupPlansPage = withAnalyticsPageEvents(PageType.MANAGE_GROUP_PLANS)(ManageGroupPlans);
const AdminOrganizationsPage = withAnalyticsPageEvents(PageType.ADMIN_ORGANIZATIONS)(AdminOrganizations);
const AdminUsersPermissionsPage = withAnalyticsPageEvents(PageType.ADMIN_USERS)(AdminUserPermissions);
const AdminPermissionRolesPage = withAnalyticsPageEvents(PageType.ADMIN_PERMISSION_ROLES)(AdminPermissionRoles);
const ManageOrganizationGeneralPage = withAnalyticsPageEvents(PageType.MANAGE_ORGANIZATION_GENERAL)(ManageOrganizationGeneral);
const ManageOrganizationMembersPage = withAnalyticsPageEvents(PageType.MANAGE_ORGANIZATION_MEMBERS)(ManageOrganizationMembers);
const LLMPromptsPage = withAnalyticsPageEvents(PageType.LLM_PROMPTS)(LLMPromptsManager);

export const routesForPublic: RouteObject[] = [
  { path: '/emailConfirmation/:token?', Component: EmailConfirmationTokenPage },
  { path: RouteNames.FORGOT_PASSWORD, Component: ForgotPasswordPage },
  { path: RouteNames.FORGOT_PASSWORD_EMAIL, Component: ForgotPasswordEmailPage },
  { path: RouteNames.RESET_PASSWORD, Component: ResetPasswordPage },
  { path: RouteNames.CREATE_PASSWORD, Component: CreatePasswordPage },
  { path: RouteNames.INVITE, Component: InviteAccountPage },
  { path: RouteNames.PHONE_VERIFICATION, Component: PhoneVerificationPage },
  { path: RouteNames.SUCCESS_AUTH, Component: SuccessAuthPage },
  { path: RouteNames.DELETE_ACCOUNT, Component: DeleteAccountPage },
  { path: RouteNames.DELETE_ACCOUNT_CONFIRM, Component: DeleteAccountConfirmPage }
];

export const routesForNotAuthenticatedOnly: RouteObject[] = [
  {
    element: <RedirectIfLoggedInRoute />, // Wrap the component in RedirectIfLoggedInRoute
    children: [
      { path: RouteNames.LOGIN, Component: LoginPage },
      { path: '*', Component: RouteNotFound }
    ]
  }
];

export const routesForAuthenticatedOnly: RouteObject[] = [
  {
    path: RouteNames.HOME,
    element: <ProtectedRoute />, // Wrap the component in ProtectedRoute
    children: [
      { path: RouteNames.HOME, Component: MainMenuPage },
      { path: RouteNames.KOKO_MAP, Component: KokoMapPage },
      { path: RouteNames.DEVICE_OVERVIEW, Component: DeviceOverviewPage },
      { path: RouteNames.DEVICE_FACTORY_RESET_HISTORY, Component: DeviceFactoryResetHistoryPage },
      { path: RouteNames.DEVICE_UNLINK_HISTORY, Component: DeviceUnlinkHistoryPage },
      // { path: RouteNames.PERSON_PERMISSIONS, Component: PersonPermissionsPage },
      { path: RouteNames.PERSON_PROGRAM, Component: PersonProgram },
      { path: RouteNames.USERS, Component: UsersPage },
      { path: RouteNames.ALL_USERS, Component: AllUsersPage },
      { path: RouteNames.FLAGGED_USERS, Component: FlaggedUsersPage },
      { path: RouteNames.USER_DETAILS, Component: UserDetailsPage },
      { path: RouteNames.USER_DETAILS_SLEEP_COACH_CHAT, Component: UserDetailsSleepCoachChatPage },
      { path: RouteNames.DEMO_ACCESS_CODES, Component: DemoAccessCodesPage },
      { path: RouteNames.DEVICE_PROVISIONING, Component: DeviceProvisioningPage },
      { path: RouteNames.FIRMWARE_VERSIONS, Component: FirmwareVersionsPage },
      { path: RouteNames.CONTENT_MANAGER, Component: ContentManagerPage },
      { path: RouteNames.ALL_TRACKS, Component: ContentAllTracksPage },
      { path: RouteNames.ALL_CONTENT, Component: ContentAllContentPage },
      { path: RouteNames.TRACK, Component: ContentTrackPage },
      { path: RouteNames.DEVICE_CONTENT_DETAILS, Component: ContentDeviceContentDetailsPage },
      { path: RouteNames.PAGE_DETAILS_UPDATE, Component: ContentPageDetailsUpdatePage },
      { path: RouteNames.PAGE_DETAILS_ADD, Component: ContentPageDetailsAddPage },
      { path: RouteNames.FLAG_DETAILS, Component: FlagDetailsPage },
      { path: RouteNames.ALL_FLAGS, Component: AllFlagsPage },
      { path: RouteNames.NEW_SLEEP_COACH_CHATS, Component: NewSleepCoachChatsPage },
      { path: RouteNames.SLEEP_SCHEDULE_REVIEW, Component: SleepSchedulesReviewPage },
      { path: RouteNames.SLEEP_SCHEDULE_REVIEW_DETAILS, Component: SleepSchedulesReviewPage },
      { path: RouteNames.LLM_PROMPTS, Component: LLMPromptsPage },
      { path: '*', Component: LoginPage }
    ]
  }
];

export const routesForAuthenticatedOnlyNewDashboard: RouteObject[] = [
  {
    path: RouteNames.HOME,
    element: <ProtectedRoute />, // Wrap the component in ProtectedRoute
    children: [
      {
        path: RouteNames.HOME,
        children: [
          {
            // When the path is '/' show AllPatientsPage
            index: true,
            Component: AllPatientsPage
          },
          // { path: RouteNames.DASHBOARD, Component: MainDashboardPage },
          { path: RouteNames.ALL_PATIENTS, Component: AllPatientsPage },
          // { path: RouteNames.PATIENT_DETAILS, Component: PatientProfileOldPage },
          { path: RouteNames.PATIENT_DASHBOARD, Component: PatientDashboardPage },
          { path: RouteNames.EDIT_PATIENT, Component: EditPatientPage },
          { path: RouteNames.NEW_PATIENT, Component: NewPatientPage },
          { path: RouteNames.NEW_DTC_PATIENT, Component: NewDTCPatientPage },
          { path: RouteNames.ASSIGN_CARE_PLAN, Component: AssignCarePlanPage },
          { path: RouteNames.NEW_ACTIVITY, Component: NewActivityPage },
          { path: RouteNames.CARE_TEAM, Component: CareTeamPage },
          { path: RouteNames.CARE_GROUP, Component: CareGroupPage },
          { path: RouteNames.MANAGE_CARE_PLANS, Component: ManageGroupPlansPage },
          { path: RouteNames.NEW_TEAM_MEMBER, Component: NewTeamMemberPage },
          { path: RouteNames.TASKS, Component: TasksPage },
          { path: RouteNames.INBOX, Component: InboxPage }
        ]
      },
      {
        path: RouteNames.ADMIN,
        element: <AdminSideBar />,
        children: [
          {
            // When the path is '/' show AllPatientsPage
            index: true,
            Component: AdminOrganizationsPage
          },
          { path: RouteNames.ADMIN_ORGANIZATIONS, Component: AdminOrganizationsPage },
          { path: RouteNames.ADMIN_USERS, Component: AdminUsersPermissionsPage },
          { path: RouteNames.ADMIN_PERMISSION_ROLES, Component: AdminPermissionRolesPage }
        ]
      },
      {
        path: RouteNames.MANAGE_ORGANIZATION,
        Component: ManageOrganizationLayout,
        children: [
          {
            index: true,
            Component: ManageOrganizationGeneralPage
          },
          { path: RouteNames.MANAGE_ORGANIZATION_GENERAL, Component: ManageOrganizationGeneralPage },
          { path: RouteNames.MANAGE_ORGANIZATION_MEMBERS, Component: ManageOrganizationMembersPage }
        ]
      },
      // old routes below:
      { path: RouteNames.NEW_DTC_PATIENT, Component: NewDTCPatientPage },
      { path: RouteNames.HOME_OLD, Component: MainMenuPage },
      { path: RouteNames.KOKO_MAP, Component: KokoMapPage },
      { path: RouteNames.DEVICE_OVERVIEW, Component: DeviceOverviewPage },
      { path: RouteNames.DEVICE_FACTORY_RESET_HISTORY, Component: DeviceFactoryResetHistoryPage },
      { path: RouteNames.DEVICE_UNLINK_HISTORY, Component: DeviceUnlinkHistoryPage },
      // { path: RouteNames.PERSON_PERMISSIONS, Component: PersonPermissionsPage },
      { path: RouteNames.PERSON_PROGRAM, Component: PersonProgram },
      { path: RouteNames.USERS, Component: UsersPage },
      { path: RouteNames.ALL_USERS, Component: AllUsersPage },
      { path: RouteNames.FLAGGED_USERS, Component: FlaggedUsersPage },
      { path: RouteNames.USER_DETAILS, Component: UserDetailsPage },
      { path: RouteNames.USER_DETAILS_SLEEP_COACH_CHAT, Component: UserDetailsSleepCoachChatPage },
      { path: RouteNames.DEMO_ACCESS_CODES, Component: DemoAccessCodesPage },
      { path: RouteNames.DEVICE_PROVISIONING, Component: DeviceProvisioningPage },
      { path: RouteNames.FIRMWARE_VERSIONS, Component: FirmwareVersionsPage },
      { path: RouteNames.CONTENT_MANAGER, Component: ContentManagerPage },
      { path: RouteNames.ALL_TRACKS, Component: ContentAllTracksPage },
      { path: RouteNames.ALL_CONTENT, Component: ContentAllContentPage },
      { path: RouteNames.TRACK, Component: ContentTrackPage },
      { path: RouteNames.DEVICE_CONTENT_DETAILS, Component: ContentDeviceContentDetailsPage },
      { path: RouteNames.PAGE_DETAILS_UPDATE, Component: ContentPageDetailsUpdatePage },
      { path: RouteNames.PAGE_DETAILS_ADD, Component: ContentPageDetailsAddPage },
      { path: RouteNames.FLAG_DETAILS, Component: FlagDetailsPage },
      { path: RouteNames.ALL_FLAGS, Component: AllFlagsPage },
      { path: RouteNames.NEW_SLEEP_COACH_CHATS, Component: NewSleepCoachChatsPage },
      { path: RouteNames.SLEEP_SCHEDULE_REVIEW, Component: SleepSchedulesReviewPage },
      { path: RouteNames.SLEEP_SCHEDULE_REVIEW_DETAILS, Component: SleepSchedulesReviewPage },
      { path: RouteNames.LLM_PROMPTS, Component: LLMPromptsPage },
      { path: '*', Component: LoginPage }
    ]
  }
];
